import { useEffect, useState, useCallback, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import FileUploader from "../uploader/fileUploader";
import { downloadBoxFile } from "../utilities/downloadFiles";
import { fetchBoxAccessToken } from "../../store/common/authSlice";
import { useParams } from "react-router-dom";
// import { fetchCaseById } from "../../store/user/userSlice";
import { fetchCaseFilesByCaseId } from "../../store/common/fetchBoxFileSlice";
import {
  deleteBoxFiles,
  resetDeleteState,
} from "../../store/common/deleteBoxFileSlice";
import { useToast } from "../utilities/Toast";
import ConfirmModal from "../utilities/confirmModal";
import FileCheckStatus from "./FileCheckStatus";
import { formatDateTimeIST } from "../../utils/dateTime";

interface FileState {
  downloading: boolean;
  downloaded: boolean;
  error: boolean;
  previewing: boolean;
}

const Downloads = () => {
  const dispatch = useAppDispatch();
  const { caseFiles, fetching } = useAppSelector((s) => s.fileUpload);
  const { user, boxAccessToken } = useAppSelector((s) => s.auth);
  const { employee } = useAppSelector((s) => s.allocationAuth);
  const { selectedCase, isLoading } = useAppSelector((s) => s.adminCases);
  const { access } = useAppSelector((s) => s.tsUploadAccess);
  const {
    loading: deleting,
    success: deleteSuccess,
    error: deleteError,
  } = useAppSelector((s) => s.deleteBoxFiles);
  const { showToast } = useToast();
  const { case_id } = useParams<{ case_id: string }>();

  const [fileStates, setFileStates] = useState<Record<number, FileState>>({});
  const [selectedClientFile] = useState("");

  useEffect(() => {
    if (!case_id) return;
    const fetchAll = async () => {
      try {
        await Promise.all([
          dispatch(fetchBoxAccessToken()),
          // dispatch(fetchCaseById(case_id)),
          dispatch(fetchCaseFilesByCaseId({ case_id })),
        ]);
      } catch (err) {
        console.error(err);
      }
    };
    fetchAll();
  }, [case_id, dispatch]);

  const downloadFiles = useMemo(
    () => caseFiles.filter((f) => f.file_type?.toLowerCase() === "ts_download"),
    [caseFiles]
  );

  const uniqueFolders = useMemo(() => {
    const folders = caseFiles
      .filter((f) => f.case_id === case_id && f.folder_name)
      .map((f) => f.folder_name);
    return Array.from(new Set(folders));
  }, [caseFiles, case_id]);

  const filesByFolder = useMemo(() => {
    return downloadFiles.reduce<Record<string, typeof downloadFiles>>(
      (acc, f) => {
        const folder = f.folder_name || "Unknown";
        if (!acc[folder]) acc[folder] = [];
        acc[folder].push(f);
        return acc;
      },
      {}
    );
  }, [downloadFiles]);

  const updateFileState = useCallback(
    (fileId: number, changes: Partial<FileState>) => {
      setFileStates((prev) => ({
        ...prev,
        [fileId]: { ...prev[fileId], ...changes },
      }));
    },
    []
  );

  const handleDownload = useCallback(
    async (fileId: number, fileName: string) => {
      if (!boxAccessToken) return;
      updateFileState(fileId, { downloading: true, error: false });
      try {
        await downloadBoxFile(fileId.toString(), fileName, boxAccessToken);
        updateFileState(fileId, { downloaded: true });
      } catch {
        updateFileState(fileId, { error: true });
      } finally {
        updateFileState(fileId, { downloading: false });
      }
    },
    [boxAccessToken, updateFileState]
  );

  const handlePreview = useCallback(
    async (fileId: number) => {
      if (!boxAccessToken) return;
      updateFileState(fileId, { previewing: true });
      try {
        const res = await fetch(
          `https://api.box.com/2.0/files/${fileId}/content`,
          {
            headers: { Authorization: `Bearer ${boxAccessToken}` },
          }
        );
        const html = await res.text();
        const win = window.open("", "_blank");
        win?.document.write(html);
        win?.document.close();
      } catch {
        alert("Preview failed");
      } finally {
        updateFileState(fileId, { previewing: false });
      }
    },
    [boxAccessToken, updateFileState]
  );

  const handleStlView = useCallback((fileId: number, fileName: string) => {
    const params = new URLSearchParams({ name: fileName });
    window.open(
      `/dashboard/stl-viewer/${fileId}?${params.toString()}`,
      "_blank",
      "noopener,noreferrer"
    );
  }, []);

  // ---- Delete Mode ----
  const [deleteMode, setDeleteMode] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState<number[]>([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const toggleFileSelect = (fileId: number) => {
    setSelectedForDelete((prev) =>
      prev.includes(fileId)
        ? prev.filter((id) => id !== fileId)
        : [...prev, fileId]
    );
  };

  // 👉 NEW FUNCTION: Get all files in a folder
  const getFileIdsInFolder = useCallback(
    (folder: string) => {
      return (filesByFolder[folder] || []).map((f) => f.fileId);
    },
    [filesByFolder]
  );

  const handleDeleteClick = () => {
    if (!selectedForDelete.length) return;
    setShowConfirmModal(true);
  };

  const allFileIds = useMemo(
    () => downloadFiles.map((f) => f.fileId),
    [downloadFiles]
  );

  const allSelectedGlobally = useMemo(
    () =>
      allFileIds.length > 0 &&
      allFileIds.every((id) => selectedForDelete.includes(id)),
    [allFileIds, selectedForDelete]
  );

  const toggleSelectAllGlobal = () => {
    if (allSelectedGlobally) {
      // Unselect ALL
      setSelectedForDelete([]);
    } else {
      // Select ALL
      setSelectedForDelete([...allFileIds]);
    }
  };

  const handleConfirmDelete = async () => {
    setShowConfirmModal(false);
    await dispatch(
      deleteBoxFiles({
        fileIds: selectedForDelete.map(String),
        deleteBy:
          user?.role === "Super Admin"
            ? user?.user_name
            : employee?.emp_name || "",
      })
    );
    setSelectedForDelete([]);
  };

  useEffect(() => {
    if (deleteSuccess && case_id) {
      dispatch(fetchCaseFilesByCaseId({ case_id }));
      dispatch(resetDeleteState());
      setDeleteMode(false);
      showToast("File(s) deleted successfully!", "success");
    }
  }, [deleteSuccess, dispatch, case_id, showToast]);

  useEffect(() => {
    if (deleteError) {
      showToast(`Failed to delete files: ${deleteError}`, "error");
      dispatch(resetDeleteState());
    }
  }, [deleteError, dispatch, showToast]);

  if (isLoading || fetching)
    return <p className="text-gray-500 italic">Loading Downloads...</p>;

  return (
    <div className="flex flex-col text-sm md:flex-row gap-6 p-4 min-h-[60vh]">
      {/* LEFT PANEL */}
      <div className="w-full md:w-1/2 flex flex-col gap-4 text-gray-800">
        {downloadFiles && downloadFiles.length > 0 && (
          <div className="flex justify-between items-center mb-2 text-white">
            {user &&
              !["Admin Read", "Production", "Side Admin"].includes(user.role) && (
                <div className="space-x-2">
                  {!deleteMode ? (
                    <button
                      onClick={() => setDeleteMode(true)}
                      className="bg-red-500 px-4 py-1 rounded-lg hover:bg-red-600 cursor-pointer"
                    >
                      Delete Files
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={handleDeleteClick}
                        disabled={deleting || selectedForDelete.length === 0}
                        className="bg-green-500 px-4 py-1 rounded-lg hover:bg-green-600 cursor-pointer"
                      >
                        Confirm Delete
                      </button>
                      <button
                        onClick={() => {
                          setDeleteMode(false);
                          setSelectedForDelete([]);
                        }}
                        className="bg-gray-500 py-1 px-4 rounded-lg hover:bg-gray-600 cursor-pointer"
                      >
                        Cancel
                      </button>

                      {deleteMode && (
                        <label className="flex items-center gap-2 ml-4 mt-2 text-sm text-black">
                          <input
                            type="checkbox"
                            checked={allSelectedGlobally}
                            onChange={toggleSelectAllGlobal}
                          />
                          Select All Files
                        </label>
                      )}
                    </>
                  )}
                </div>
              )}
          </div>
        )}

        {/* PER-FOLDER LIST */}
        {Object.entries(filesByFolder)
          .filter(
            ([folder]) => !selectedClientFile || folder === selectedClientFile
          )
          .map(([folder, files]) => {
            const fileIdsInFolder = getFileIdsInFolder(folder);
            const allSelectedInFolder = fileIdsInFolder.every((id) =>
              selectedForDelete.includes(id)
            );

            const toggleSelectAllInFolder = () => {
              if (allSelectedInFolder) {
                setSelectedForDelete((prev) =>
                  prev.filter((id) => !fileIdsInFolder.includes(id))
                );
              } else {
                setSelectedForDelete((prev) => [
                  ...new Set([...prev, ...fileIdsInFolder]),
                ]);
              }
            };

            return (
              <div key={folder} className="m2-4">
                <h3 className="font-bold mb-1 flex items-center gap-2">
                  {deleteMode && (
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={allSelectedInFolder}
                        onChange={toggleSelectAllInFolder}
                      />
                      Select All in
                    </label>
                  )}
                  {folder}
                </h3>

                {files.map((f) => {
                  const state = fileStates[f.fileId] || {};
                  const lowerName = f.file_name.toLowerCase();
                  const isHtml = lowerName.endsWith(".html");
                  const isStl = lowerName.endsWith(".stl");
                  const isSelected = selectedForDelete.includes(f.fileId);

                  return (
                    <div
                      key={f.fileId}
                      className={`flex gap-3 items-center px-2 rounded ${
                        deleteMode && isSelected ? "bg-red-100" : ""
                      }`}
                    >
                      {deleteMode && (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleFileSelect(f.fileId)}
                        />
                      )}

                      <button
                        onClick={() =>
                          !deleteMode && handleDownload(f.fileId, f.file_name)
                        }
                        disabled={state.downloading || deleteMode}
                        className={`text-left ${
                          deleteMode
                            ? "text-gray-700"
                            : "text-[#1DA8C6] hover:underline"
                        }`}
                      >
                        <span className="block">{f.file_name}</span>
                        {f.creation_ts && (
                          <span className="block text-[11px] text-gray-500 no-underline">
                            Uploaded: {formatDateTimeIST(f.creation_ts)} IST
                          </span>
                        )}
                      </button>

                      {state.downloading && (
                        <span className="italic text-gray-500">
                          Downloading...
                        </span>
                      )}
                      {state.downloaded && (
                        <span className="italic text-green-500">
                          Downloaded
                        </span>
                      )}
                      {state.error && (
                        <span className="italic text-red-500">Failed</span>
                      )}

                      {isHtml && !deleteMode && (
                        <button
                          onClick={() => handlePreview(f.fileId)}
                          disabled={state.previewing}
                          className="text-gray-500 hover:underline"
                        >
                          {state.previewing ? "Loading..." : "[ Preview ]"}
                        </button>
                      )}

                      {isStl && !deleteMode && (
                        <button
                          onClick={() => handleStlView(f.fileId, f.file_name)}
                          className="text-gray-500 hover:underline"
                        >
                          [ View STL ]
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}

        {downloadFiles.length === 0 && !fetching && (
          <p className="italic text-gray-500">No downloads uploaded yet.</p>
        )}
      </div>

      {/* RIGHT PANEL */}
      <div className="w-full md:w-1/2 flex flex-col gap-4">
        {selectedCase?.status === "Files pending" ? (
          <p className="text-red-400">Please upload client files.</p>
        ) : selectedCase?.service_type === "filledOrder" &&
          uniqueFolders.length === 0 ? (
          <p className="text-orange-500 font-medium">
            Multiple-unit case — create a TS folder for upload.
          </p>
        ) : (
          user &&
          !["Admin Read", "Production", "Side Admin"].includes(user.role) && (
            <FileUploader
              userId={user?.user_id || 0}
              caseId={selectedCase?.case_id || ""}
              caseOwner={(selectedCase?.case_owner || "").replace(/,/g, " ")}
              caseFolderId={selectedCase?.box_folder_id?.toString() || ""}
              file_type="TS_DOWNLOAD"
              accessToken={boxAccessToken || ""}
              patientFolderName={ (selectedClientFile || "") .replace(/\.zip$/i, "") .replace(/,/g, " ") .replace(/\s+/g, " ") .trim() }
              disabled={
                user.role !== "Super Admin" &&
                access?.allowed !== true &&
                selectedCase?.status?.toLowerCase() !== "completed"
              }
              disabledReason={access?.message}
            />
          )
        )}
        <FileCheckStatus caseId={case_id || selectedCase?.case_id} />
      </div>

      {/* CONFIRM MODAL */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmDelete}
        loading={deleting}
      />
    </div>
  );
};

export default Downloads;
